/**
 * @file 预约权益区组件
 * @description 使用 Swiper 轮播展示用户各等级的权益卡片
 */

import React, { useRef, useEffect } from "react";
import { Swiper, SwiperRef } from "antd-mobile";
import { LEVEL_LIST } from "../../../../config";
import { formatNumber } from "@/utils/common";
import "./index.less";

/**
 * 权益项接口
 */
export interface PrivilegeItem {
  level: number; // 等级数字（1、2、3）
  levelName: string; // 等级名称，如"高压小鹏车主V2等级专享"
  levelType: string; // 等级类型
  monthlyQuota: number; // 每月预约次数配额，-1表示无限制
  usedCount: number; // 本月已使用次数
  remainingCount: number; // 剩余次数，-1表示无限制
  isCurrentLevel: boolean; // 是否为当前用户等级
}

/**
 * 用户等级信息接口（用于计算升级度数）
 */
export interface UserLevelInfoForPrivilege {
  level?: number; // 当前等级
  powerValueSum?: number; // 近90天会员电量
}

/**
 * BookingPrivilege 组件 Props 接口
 * 支持多等级轮播展示
 */
export interface BookingPrivilegeProps {
  privileges: PrivilegeItem[]; // 所有等级的权益信息列表
  currentLevelIndex: number; // 当前用户等级在列表中的索引
  userLevelInfo?: UserLevelInfoForPrivilege | null; // 用户等级信息（用于计算升级度数）
  onSwiperChange?: (index: number) => void; // 轮播切换回调（用于埋点）
}

/**
 * 获取配额显示文案
 * 规则：
 * - quota === -1：无限次预约机会/月
 * - quota === 0：暂未开放预约特权
 * - quota > 0：{N}次预约机会/月
 */
export const getQuotaText = (quota: number): string => {
  if (quota === -1) {
    return "无限次预约机会/月";
  }
  if (quota === 0) {
    return "暂未开放预约特权";
  }
  return `${quota}次预约机会/月`;
};

/**
 * 获取副标题文案
 * 规则（按图片需求）：
 * 1. 查看低等级（当前等级 > 卡片等级）：显示 "(当前等级高于此等级)"
 * 2. 查看高等级（当前等级 < 卡片等级）：显示 "(再充xxx度电可升级)"
 * 3. 查看当前等级且配置次数为0（monthlyQuota === 0）：不展示副标题
 * 4. 查看当前等级且预约次数不限（monthlyQuota === -1）：
 *    - 本月已使用（usedCount > 0）：显示 "(本月预约机会已使用x次)"
 *    - 本月未使用（usedCount === 0）：显示 "(本月还未使用)"
 * 5. 查看当前等级且预约次数有限：
 *    - 本月已用完（remainingCount === 0）：显示 "(本月预约机会已用完)"
 *    - 本月未使用（usedCount === 0）：显示 "(本月还未使用)"
 *    - 本月已使用但还有机会（remainingCount > 0）：显示 "(当前剩余N次)"
 */
export const getSubtitleText = (
  cardLevel: number,
  currentLevel: number,
  powerValueSum: number,
  remainingCount: number,
  monthlyQuota: number,
  usedCount: number
): string => {
  // 查看低等级
  if (currentLevel > cardLevel) {
    return "(当前等级高于此等级)";
  }

  // 查看高等级
  if (currentLevel < cardLevel) {
    // 计算升级所需度数
    const targetLevelConfig = LEVEL_LIST.find((item) => item.level === cardLevel);
    if (targetLevelConfig) {
      const needPower = targetLevelConfig.limit - powerValueSum;
      if (needPower > 0) {
        return `(再充${formatNumber(needPower, 1)}度电可升级)`;
      }
    }
    return "(再充电可升级)";
  }

  // 查看当前等级
  // 配置次数为0时，不展示副标题
  if (monthlyQuota === 0) {
    return "";
  }

  // 预约次数不限时，显示已使用次数
  if (monthlyQuota === -1 || remainingCount === -1) {
    // 如果本月已使用过，显示已使用次数
    if (usedCount > 0) {
      return `(本月预约机会已使用${usedCount}次)`;
    }
    // 本月未使用
    return "(本月还未使用)";
  }

  // 本月已用完
  if (remainingCount === 0) {
    return "(本月预约机会已用完)";
  }

  // 本月未使用
  if (usedCount === 0) {
    return "(本月还未使用)";
  }

  // 本月已使用但还有机会
  return `(当前剩余${remainingCount}次)`;
};

/** 左箭头图片地址 */
const ARROW_LEFT_URL = "https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/arrow-group-left.svg";
/** 右箭头图片地址 */
const ARROW_RIGHT_URL = "https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/arrow-group-right.svg";

/**
 * 预约权益区组件
 * 使用 Swiper 轮播展示用户各等级的权益卡片
 * 默认定位到当前用户等级，支持左右滑动切换
 */
const BookingPrivilege: React.FC<BookingPrivilegeProps> = ({
  privileges,
  currentLevelIndex,
  userLevelInfo,
  onSwiperChange,
}) => {
  const swiperRef = useRef<SwiperRef>(null);
  const [activeIndex, setActiveIndex] = React.useState(currentLevelIndex >= 0 ? currentLevelIndex : 0);
  // 记录上一次的等级索引，用于判断是否需要自动切换
  const prevLevelIndexRef = useRef<number>(currentLevelIndex);

  // 获取当前用户等级和电量
  const currentLevel = userLevelInfo?.level || 1;
  const powerValueSum = userLevelInfo?.powerValueSum || 0;

  // 监听 currentLevelIndex 变化，自动定位到当前用户等级
  // 处理异步获取 levelInfo 和页面返回刷新的场景
  useEffect(() => {
    if (currentLevelIndex >= 0 && currentLevelIndex !== prevLevelIndexRef.current) {
      prevLevelIndexRef.current = currentLevelIndex;
      setActiveIndex(currentLevelIndex);
      swiperRef.current?.swipeTo(currentLevelIndex);
    }
  }, [currentLevelIndex]);

  // 如果没有权益数据，不渲染
  if (!privileges || privileges.length === 0) {
    return null;
  }

  /**
   * 处理轮播切换
   * @param index - 切换后的卡片索引
   */
  const handleIndexChange = (index: number) => {
    setActiveIndex(index);
    if (onSwiperChange) {
      onSwiperChange(index);
    }
  };

  /**
   * 点击左箭头，切换到上一张卡片
   */
  const handlePrev = () => {
    if (activeIndex > 0) {
      swiperRef.current?.swipeTo(activeIndex - 1);
    }
  };

  /**
   * 点击右箭头，切换到下一张卡片
   */
  const handleNext = () => {
    if (activeIndex < privileges.length - 1) {
      swiperRef.current?.swipeTo(activeIndex + 1);
    }
  };

  /**
 * 渲染单个权益卡片
 * @param privilege - 权益数据
 * @returns 权益卡片 JSX
 */
  const renderPrivilegeCard = (privilege: PrivilegeItem) => {
    const { level: cardLevel, monthlyQuota, remainingCount, usedCount } = privilege;
    const quotaText = getQuotaText(monthlyQuota);

    // 根据规则获取副标题
    const subtitleText = getSubtitleText(
      cardLevel,
      currentLevel,
      powerValueSum,
      remainingCount,
      monthlyQuota,
      usedCount
    );

    return (
      <div
        className="privilege-card"
        style={{
          backgroundImage: `url(https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/book-v${cardLevel}-2.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="privilege-card-content">
          <span className={`privilege-card-main level-${cardLevel}`}>
            <span className="quota-text">{quotaText}</span>
            <span className="level-text"> · V{cardLevel}等级专享</span>
          </span>
          <span className="privilege-status-fixed">{subtitleText}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="booking-privilege">
      {/* 卡片区域容器 - 包含箭头和轮播 */}
      <div className="privilege-card-area">
        {/* 左箭头按钮 - 固定悬浮 */}
        <div
          className={`arrow-btn arrow-left ${activeIndex === 0 ? "disabled" : ""}`}
          onClick={handlePrev}
        >
          <img src={ARROW_LEFT_URL} alt="上一个" />
        </div>

        {/* 右箭头按钮 - 固定悬浮 */}
        <div
          className={`arrow-btn arrow-right ${activeIndex === privileges.length - 1 ? "disabled" : ""}`}
          onClick={handleNext}
        >
          <img src={ARROW_RIGHT_URL} alt="下一个" />
        </div>

        <Swiper
          ref={swiperRef}
          defaultIndex={currentLevelIndex >= 0 ? currentLevelIndex : 0}
          onIndexChange={handleIndexChange}
          indicator={() => null}
          loop={false}
          className="privilege-swiper"
        >
          {privileges.map((privilege, index) => (
            <Swiper.Item key={`${privilege.levelType}-${index}`}>
              {renderPrivilegeCard(privilege)}
            </Swiper.Item>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default BookingPrivilege;
